import {Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {BdManualContractDecisionService} from '../../../service/api/bd-manual-contract-decision.service';

/**
 * LV-specific decision option exposed via the per-row dropdown.
 */
export type BdLvDecisionAction =
    | 'PREMIUM_RELEASE'
    | 'BUYBACK'
    | 'DUNNING_WAIVER'
    | 'OTHER';

interface BdLvDecisionRow {
    contractId: number;
    contractName: string;
    dunningBalance: number;
    selectedAction: BdLvDecisionAction | null;
    selected: boolean;
    submitted: boolean;
}

interface DecisionOption {
    label: string;
    value: BdLvDecisionAction;
}

/**
 * AI:
 * Status: 'wave-3-implementation'
 * Type: Component
 * SubType: Exit-UI / Decision-Table
 * Reason: LV-specific decision table — only LV contracts are listed. The agent picks an
 *         outcome per row (Prämienfreistellung / Rückkauf / Mahnverzicht / Sonstiges)
 *         and submits the whole batch to {@code POST /manual-contract-decision/lv-transfer}.
 *
 *         Mapped to {@code BdDunningExitUiComponent.LV_DECISION_TABLE}.
 *
 * @ticket TM-1000001786
 */
@Component({
    selector: 'bd-lv-decision-table',
    templateUrl: './bd-lv-decision-table.component.html',
    styleUrls: ['./bd-lv-decision-table.component.scss'],
    standalone: false
})
export class BdLvDecisionTableComponent implements OnInit {

    // ----------------------------------------------------------------- //
    // Inputs
    // ----------------------------------------------------------------- //

    @Input() dunning: Record<string, any> | null = null;
    @Input() contracts: any[] | null = null;
    @Input() uiComponentConfig: Record<string, any> | null = null;

    // U17: Vom Drawer (ngComponentOutletInputs) durchgereichte Felder. Nicht
    // direkt verwendet, aber als @Input() deklariert, damit die Drawer-Bindung
    // kein NG0303 auslöst.
    @Input() dunningId: number | null = null;
    @Input() handOvers: unknown[] | null = null;
    @Input() activePause: unknown | null = null;
    @Input() breachedPlan: unknown | null = null;
    @Input() currentUserId: number | null = null;

    // ----------------------------------------------------------------- //
    // Outputs
    // ----------------------------------------------------------------- //

    @Output() actionSubmitted = new EventEmitter<{contractIds: number[]}>();

    // ----------------------------------------------------------------- //
    // State
    // ----------------------------------------------------------------- //

    initialized: boolean = false;
    submitting: boolean = false;
    errorMessage: string | null = null;
    success: boolean = false;

    rows: BdLvDecisionRow[] = [];

    /**
     * C19: Anzahl der LV-Posten (→ SB-Arbeitsvorrat) auf dem Konto.
     */
    lvCount: number = 0;

    /**
     * C19: Anzahl der Nicht-LV-Posten (→ regulärer Mahnlauf) auf dem Konto.
     * > 0 zeigt an, dass es sich um ein Mischkonto handelt.
     */
    nonLvCount: number = 0;

    readonly decisionOptions: DecisionOption[] = [
        {label: 'Prämienfreistellung', value: 'PREMIUM_RELEASE'},
        {label: 'Rückkauf',            value: 'BUYBACK'},
        {label: 'Mahnverzicht',        value: 'DUNNING_WAIVER'},
        {label: 'Sonstiges',           value: 'OTHER'}
    ];

    constructor(private readonly manualContractDecisionService: BdManualContractDecisionService) {}

    // ----------------------------------------------------------------- //
    // Lifecycle
    // ----------------------------------------------------------------- //

    ngOnInit(): void {
        this.rows = this.buildRows(this.contracts);
        // C19: Mischkonto-Aufteilung ermitteln (LV → SB-Liste / Rest → regulär).
        const all = this.contracts ?? [];
        this.lvCount = all.filter(c => this.isLvContract(c)).length;
        this.nonLvCount = all.length - this.lvCount;
        this.initialized = true;
    }

    /**
     * C19: true, wenn das Konto sowohl LV- als auch Nicht-LV-Posten enthält
     * (Mischkonto). Steuert den Aufteilungs-Hinweis im Template.
     */
    get isMixedAccount(): boolean {
        return this.lvCount > 0 && this.nonLvCount > 0;
    }

    // ----------------------------------------------------------------- //
    // Actions
    // ----------------------------------------------------------------- //

    selectAll(checked: boolean): void {
        this.rows.forEach(r => {
            if (!r.submitted) {
                r.selected = checked;
            }
        });
    }

    get allSelected(): boolean {
        const eligible = this.rows.filter(r => !r.submitted);
        return eligible.length > 0 && eligible.every(r => r.selected);
    }

    get selectedRows(): BdLvDecisionRow[] {
        return this.rows.filter(r => r.selected && !r.submitted);
    }

    /**
     * Submits the LV transfer for the selected contracts.
     */
    submitTransfer(): void {
        const dunningId = this.dunning?.['id'] ?? this.dunning?.['dunningId'];
        if (dunningId == null) {
            this.errorMessage = 'Keine Mahnung referenziert.';
            return;
        }
        const ids = this.selectedRows.map(r => r.contractId);
        if (ids.length === 0) {
            this.errorMessage = 'Bitte mindestens einen LV-Vertrag auswählen.';
            return;
        }
        this.errorMessage = null;
        this.submitting = true;

        this.manualContractDecisionService.transferLvToSbList(dunningId, ids).subscribe({
            next: () => {
                this.submitting = false;
                this.success = true;
                this.selectedRows.forEach(r => (r.submitted = true));
                this.actionSubmitted.emit({contractIds: ids});
            },
            error: (err) => {
                this.submitting = false;
                this.errorMessage = this.extractError(err);
            }
        });
    }

    // ----------------------------------------------------------------- //
    // Internal helpers
    // ----------------------------------------------------------------- //

    private buildRows(contracts: any[] | null): BdLvDecisionRow[] {
        if (!contracts) {
            return [];
        }
        // Filter to LV contracts only — accept multiple shape variants from the
        // upstream QL pipe so this component works regardless of the exact join.
        return contracts
            .filter(c => this.isLvContract(c))
            .map(c => ({
                contractId: c?.id ?? c?.contractId ?? 0,
                contractName: c?.identifier ?? c?.contractName ?? `LV-Vertrag ${c?.id ?? '?'}`,
                dunningBalance: c?.dunningBalance ?? c?.balance ?? 0,
                selectedAction: null,
                selected: false,
                submitted: false
            }));
    }

    private isLvContract(c: any): boolean {
        const sparte = (c?.sparte ?? c?.spartenCode ?? c?.contractTypeCode ?? '')?.toString().toUpperCase();
        if (sparte === 'LV') {
            return true;
        }
        const typeName = (c?.contractType ?? c?.contractTypeName ?? '').toString().toLowerCase();
        return typeName.includes('lebensversicherung') || typeName.includes('leben');
    }

    private extractError(err: unknown): string {
        if (err && typeof err === 'object') {
            const anyErr = err as any;
            return anyErr.error?.message ?? anyErr.message ?? 'Unbekannter Fehler.';
        }
        return 'Unbekannter Fehler.';
    }
}
